import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDownRounded';
import {
  Button,
  MenuItem, Typography,
} from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useProjectInfoContext from '../../../../../../Contexts/ProjectContexts/ProjectInfoContext/useProjectInfoContext';
import useMultiLanguageContext from '../../../../../../LiveContext/MultiLanguageContext/useMultiLanguageContext';
import { LanguageFlag } from '../../../../../MainWindow/Components/Pages/SettingsPage/SubPages/PreferencesSubPage/Components/LanguageSelector/Components/LanguageIndicator';
import StyledBox, { StyledMenu } from './styles';

function RenderProjectLanguage(props: { currentLanguage?: string, defaultLanguage: string }) {
  const { currentLanguage, defaultLanguage } = props;
  const { t: tCommon } = useTranslation('common');

  const language = React.useMemo(() => {
    if (!currentLanguage) {
      return defaultLanguage;
    }
    return currentLanguage;
  }, [currentLanguage, defaultLanguage]);

  return (
    <>
      <LanguageFlag language={currentLanguage ?? defaultLanguage} />
      <Typography>
        {tCommon(`languages.version.${(language === defaultLanguage) ? 'original' : currentLanguage}`)}
      </Typography>
    </>

  );
}

export default function SelectProjectLanguage() {
  const { project } = useProjectInfoContext();
  const {
    language, setLanguage, projectLanguageOptions,
  } = useMultiLanguageContext();
  const { t: tCommon } = useTranslation('common');

  const sortedOptions = React.useMemo(() => ([...projectLanguageOptions].sort((a, b) => {
    if (a.language === project.asrLanguage && b.language !== project.asrLanguage) return -1;
    if (b.language === project.asrLanguage && a.language !== project.asrLanguage) return 1;

    const labelA = tCommon(`languages.version.${a.language === project.asrLanguage ? 'original' : a.language}`);
    const labelB = tCommon(`languages.version.${b.language === project.asrLanguage ? 'original' : b.language}`);
    return labelA.localeCompare(labelB);
  })), [projectLanguageOptions, project?.asrLanguage]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  if (!project) {
    return null;
  }

  if (!project.translations?.length) {
    return null;
  }

  return (
    <StyledBox>
      <Button
        className={clsx('project', { active: !!anchorEl })}
        onClick={(e) => { setAnchorEl((prev) => (prev ? null : e.currentTarget)); }}
      >
        <RenderProjectLanguage currentLanguage={language} defaultLanguage={project.asrLanguage} />
        <ArrowDropDownIcon />
      </Button>
      <StyledMenu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => { setAnchorEl(null); }}
      >
        {sortedOptions.map((option) => (
          <MenuItem
            dense
            key={`${project.id}-language-${option.id}`}
            onClick={() => {
              setLanguage(option.language);
              setAnchorEl(null);
            }}
            title={language}
            selected={language === option.language}
          >
            <RenderProjectLanguage currentLanguage={option.language} defaultLanguage={project?.asrLanguage} />
          </MenuItem>
        ))}
      </StyledMenu>
    </StyledBox>
  );
}
